/**
 * emailAnalyticsApi — typed wrappers over the provider-aware analytics
 * endpoints (backend/routes/admin/adminEmailAnalytics.js).
 *
 * All four are period-parameterised and carry `provider` freshness so the UI
 * can distinguish "Brevo hasn't been polled" from "nobody opened anything".
 * Sends come from our own email_sending_log; delivered/open/click/bounce/
 * spam/unsub come from Brevo (email_provider_daily_stats, keyed by tag).
 */
import apiService from "./api";

export type EmailKind = "nurture" | "transactional";

/**
 * Wire vocabulary for `?period=` on every /email-analytics route: `today`,
 * `<N>d` (1–365), or `custom` paired with `from`&`to` as YYYY-MM-DD. Callers
 * build both halves with `emailAnalyticsPeriod()` from the shared time filter.
 */
export type Period = string;
export interface PeriodDates {
  from: string;
  to: string;
}

/** `?period=…` plus the two dates when the window is custom. */
function periodQuery(period: Period, dates?: PeriodDates): string {
  const params = new URLSearchParams({ period });
  if (dates) {
    params.set("from", dates.from);
    params.set("to", dates.to);
  }
  return params.toString();
}

export interface ProviderFreshness {
  source: "brevo";
  events_last_ok_at: string | null;
  daily_last_ok_at: string | null;
  last_error: string | null;
  events_rows?: number | null;
  daily_rows?: number | null;
  /** First day any template:<key> tag has provider data — per-template
   *  engagement is only meaningful from this date onwards. */
  template_metrics_since?: string | null;
  /**
   * Manual-sync state, derived server-side from the same sync-state row the
   * email service writes. Drives the "Sync Brevo now" button.
   *   idle | requested (queued, watcher hasn't picked it up) | running | failed
   */
  sync_state?: "idle" | "requested" | "running" | "failed";
  sync_requested_at?: string | null;
  sync_requested_by?: string | null;
}

export interface ProviderSyncResponse {
  success: boolean;
  /** false when the request was debounced rather than accepted. */
  queued?: boolean;
  reason?: "already_pending" | "synced_within_last_minute" | string;
  provider?: ProviderFreshness;
  error?: string;
}

export interface ProviderTotals {
  requests: number;
  delivered: number;
  opens: number;
  unique_opens: number;
  clicks: number;
  unique_clicks: number;
  hard_bounces: number;
  soft_bounces: number;
  spam_reports: number;
  unsubscribed: number;
  blocked: number;
}

export interface Rates {
  delivery_rate: number | null;
  open_rate: number | null;
  click_rate: number | null;
  bounce_rate: number | null;
  hard_bounce_rate: number | null;
  spam_rate: number | null;
  unsub_rate: number | null;
}

export interface SeriesPoint {
  day: string;
  sends: number;
  requests: number;
  delivered: number;
  opens: number;
  clicks: number;
  bounces: number;
}

export interface SummaryResponse {
  success: boolean;
  kind: EmailKind;
  period: string;
  provider: ProviderFreshness;
  summary: {
    sends: number;
    failed: number;
    suppressed: number;
    templates_used: number;
  } & ProviderTotals &
    Rates;
  series: SeriesPoint[];
}

export interface TemplateUsedInFlow {
  flow_id: string;
  slug: string;
  name: string;
  status: string;
  step_order: number;
  delay_hours: number;
}
export interface TemplateUsedInTrigger {
  trigger_key: string;
  event_type: string;
  is_enabled: boolean;
}

export interface TemplateRow extends ProviderTotals, Rates {
  id: string;
  template_key: string;
  template_name: string | null;
  subject_line: string | null;
  category: string | null;
  status: string | null;
  kind: EmailKind;
  updated_at: string | null;
  sends: number;
  failed: number;
  last_sent_at: string | null;
  bounces: number;
  flows: TemplateUsedInFlow[] | null;
  in_active_flow: boolean;
  triggers: TemplateUsedInTrigger[] | null;
  in_enabled_trigger: boolean;
  in_use: boolean;
}

export interface TemplatesResponse {
  success: boolean;
  kind: EmailKind;
  period: string;
  provider: ProviderFreshness;
  count: number;
  templates: TemplateRow[];
}

export interface TemplatePerformanceResponse {
  success: boolean;
  period: string;
  provider: ProviderFreshness;
  template: {
    id: string;
    template_key: string;
    template_name: string | null;
    subject_line: string | null;
    preview_text?: string | null;
    category: string | null;
    status: string | null;
    kind: EmailKind;
    updated_at: string | null;
    excerpt?: string | null;
  };
  summary: {
    sends: number;
    failed: number;
    last_sent_at: string | null;
    delivered_stitched: number;
    opened_stitched: number;
  } & ProviderTotals &
    Rates;
  series: SeriesPoint[];
  used_by: Array<{
    kind: "flow" | "trigger";
    id: string;
    key: string;
    name: string;
    status: string;
    step_order?: number | null;
    delay_hours?: number | null;
    detail?: string | null;
  }>;
  recent_events: Array<{
    occurred_at: string;
    event: string;
    email: string;
    subject?: string | null;
    reason?: string | null;
  }>;
}

export interface SequenceStep {
  flow_id: string;
  step_order: number;
  step_type: string;
  delay_hours: number;
  label: string | null;
  template_key: string | null;
  executed_period: number | string;
  failed_period: number | string;
}

export interface SequenceRow extends ProviderTotals, Rates {
  id: string;
  slug: string;
  name: string;
  status: string;
  trigger_event: string | null;
  phase_key: string | null;
  funnel_group: string | null;
  /** 'lifecycle' | 'diagnostic' are the V4 programme; 'legacy' is what it replaces. */
  funnel_class: string | null;
  description: string | null;
  updated_at: string | null;
  enrolments_total: number;
  enrolments_period: number;
  enrolments_active: number;
  last_enrolled_at: string | null;
  sends_period: number;
  last_activity_at: string | null;
  viability: "enrolling" | "quiet" | "armed" | string;
  /**
   * The audience mirroring this sequence's entry rule (migration 1102), or null
   * for event-driven sequences that have no standing population.
   */
  audience?: { id: string; slug: string; name: string; count: number } | null;
  steps: SequenceStep[];
  exits: Array<{ exit_reason: string; status: string; n: number | string }>;
}

export interface SequencesResponse {
  success: boolean;
  period: string;
  provider: ProviderFreshness;
  count: number;
  sequences: SequenceRow[];
}

const BASE = "/api/admin/email-analytics";

export async function getEmailSummary(kind: EmailKind, period: Period, dates?: PeriodDates) {
  const r = await apiService.get<SummaryResponse>(
    `${BASE}/marketing/summary?kind=${kind}&${periodQuery(period, dates)}`,
  );
  return r.data;
}

export async function getEmailTemplates(kind: EmailKind, period: Period, dates?: PeriodDates) {
  const r = await apiService.get<TemplatesResponse>(
    `${BASE}/templates?kind=${kind}&${periodQuery(period, dates)}`,
  );
  return r.data;
}

export async function getTemplatePerformance(key: string, period: Period, dates?: PeriodDates) {
  const r = await apiService.get<TemplatePerformanceResponse>(
    `${BASE}/templates/${encodeURIComponent(key)}/performance?${periodQuery(period, dates)}`,
  );
  return r.data;
}

/**
 * Ask the email service to poll Brevo now.
 *
 * Returns as soon as the request is recorded — the poll itself runs in the email
 * service and takes tens of seconds. Progress is read from `provider.sync_state`
 * on the next summary refresh, not from this response.
 */
export async function postProviderSync() {
  const r = await apiService.post<ProviderSyncResponse>(`${BASE}/provider/sync`, {});
  return r.data;
}

export async function getEmailSequences(period: Period, dates?: PeriodDates) {
  const r = await apiService.get<SequencesResponse>(
    `${BASE}/sequences?${periodQuery(period, dates)}`,
  );
  return r.data;
}
